/**
 * Validation functions for Step 1 (General Information)
 */
import { z } from "zod";
import type { TenantInfoFormData } from "../utils/formatters";

/**
 * Comprehensive HTTPS URL validation
 * Validates that the URL:
 * - Starts with https://
 * - Has a valid domain structure
 * - Optionally includes port, path, query parameters, and fragments
 */
function isValidHttpsUrl(url: string): { valid: boolean; error?: string } {
  const trimmed = url.trim();

  // Check if URL starts with https://
  if (!trimmed.startsWith("https://")) {
    return {
      valid: false,
      error: "Payslip Portal Link must start with https://",
    };
  }

  // Comprehensive regex pattern for HTTPS URLs
  // Matches: https://domain.tld (with optional www, port, path, query, fragment)
  const httpsUrlRegex =
    /^https:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?::\d{1,5})?(?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*)$/;

  if (!httpsUrlRegex.test(trimmed)) {
    return {
      valid: false,
      error: "Payslip Portal Link must be a valid HTTPS URL",
    };
  }

  // Additional validation using URL constructor for edge cases
  try {
    const parsedUrl = new URL(trimmed);
    if (parsedUrl.protocol !== "https:") {
      return {
        valid: false,
        error: "Payslip Portal Link must use HTTPS protocol",
      };
    }
    // Check if hostname is valid (not empty and has at least a TLD)
    if (!parsedUrl.hostname || parsedUrl.hostname.length < 4) {
      return {
        valid: false,
        error: "Payslip Portal Link must include a valid domain name",
      };
    }
    // Check if hostname has at least one dot (for TLD)
    if (!parsedUrl.hostname.includes(".")) {
      return {
        valid: false,
        error: "Payslip Portal Link must include a valid domain with TLD",
      };
    }
  } catch {
    return {
      valid: false,
      error: "Payslip Portal Link must be a valid URL format",
    };
  }

  return { valid: true };
}

/**
 * Validate Step 1 form data
 * Returns field-level errors
 */
export function validateStep1(
  data: TenantInfoFormData,
): Record<string, string[]> {
  const errors: Record<string, string[]> = {};

  try {
    // Create a partial schema for step 1 validation
    // We'll validate the full schema when submitting
    const step1Schema = z.object({
      tenant_name: z
        .string()
        .min(1, "Tenant name is required")
        .min(2, "Tenant name must be at least 2 characters")
        .max(100, "Tenant name cannot exceed 100 characters")
        .refine(
          (val) => /^[^\s].*[^\s]$/.test(val.trim()),
          "Tenant name cannot have leading or trailing spaces",
        ),
      type: z.enum(["CUSTOMER", "LSP"]),
      account_name: z
        .string()
        .min(1, "Account name is required")
        .min(2, "Account name must be at least 2 characters")
        .max(100, "Account name cannot exceed 100 characters")
        .refine(
          (val) => /^[a-zA-Z0-9\s\-_]+$/.test(val.trim()),
          "Account name can only contain letters, digits, spaces, hyphens, and underscores",
        )
        .refine(
          (val) => /^[^\s].*[^\s]$/.test(val.trim()),
          "Account name cannot have leading or trailing spaces",
        ),
      domains: z
        .array(z.string().min(1, "Domain cannot be empty"))
        .min(1, "At least one domain is required"),
      total_number_of_users: z
        .union([z.number(), z.string(), z.null()])
        .refine(
          (val) => {
            // Allow null, empty strings, or 0 - required check will handle this separately
            if (val === null || val === undefined) {
              return true;
            }
            if (typeof val === "string" && val.trim() === "") {
              return true;
            }
            if (val === 0) {
              return true; // Will be caught by required check
            }
            // If it's a string, validate it can be parsed as a number
            if (typeof val === "string") {
              const num = Number.parseInt(val, 10);
              return (
                !Number.isNaN(num) &&
                Number.isInteger(num) &&
                num >= 1 &&
                num <= 9999
              );
            }
            // If it's already a number, validate range
            if (typeof val === "number") {
              return Number.isInteger(val) && val >= 1 && val <= 9999;
            }
            return false;
          },
          {
            message: "Must be a number between 1 and 9999",
          },
        )
        .refine(
          (val) => {
            // Required check - must not be null, empty, undefined, or 0
            if (val === null || val === undefined) {
              return false;
            }
            if (typeof val === "string" && val.trim() === "") {
              return false;
            }
            if (val === 0) {
              return false;
            }
            return true;
          },
          { message: "Total number of users is required" },
        ),
      salesforce_id: z
        .string()
        .optional()
        .refine(
          (val) =>
            !val || val.trim() === "" || /^[a-zA-Z0-9]{20}$/.test(val.trim()),
          "Salesforce ID must be exactly 20 alphanumeric characters",
        ),
      country: z
        .string()
        .min(1, "Country is required")
        .refine((val) => val.length === 2, "Country code must be 2 characters"),
      status: z.enum(["ACTIVE", "INACTIVE"]),
      billing_start_date: z
        .date()
        .nullable()
        .refine((val) => val !== null, "Billing start date is required"),
      billing_end_date: z.date().nullable().optional(),
      supports_payroll_plus: z.boolean().nullable().optional(),
      payslip_portal_link: z.string().optional(),
    });

    // Validate the data
    const result = step1Schema.safeParse({
      tenant_name: data.tenant_name.trim(),
      type: data.type,
      account_name: data.account_name.trim(),
      domains: data.domains,
      total_number_of_users: data.total_number_of_users,
      salesforce_id: data.salesforce_id,
      country: data.country.toUpperCase(),
      status: data.status,
      billing_start_date: data.billing_start_date,
      billing_end_date: data.billing_end_date,
      supports_payroll_plus: data.supports_payroll_plus,
      payslip_portal_link: data.payslip_portal_link,
    });

    if (!result.success) {
      // Group errors by field
      result.error.errors.forEach((error) => {
        const field = error.path[0] as string;
        if (!errors[field]) {
          errors[field] = [];
        }
        errors[field].push(error.message);
      });
    }

    // Validate billing end date is after start date
    if (
      data.billing_start_date &&
      data.billing_end_date &&
      data.billing_end_date <= data.billing_start_date
    ) {
      if (!errors.billing_end_date) {
        errors.billing_end_date = [];
      }
      errors.billing_end_date.push(
        "Billing end date must be after billing start date",
      );
    }

    // Validate domains format and check for duplicates
    const domainRegex =
      /^(?=.{1,253}\.?$)(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,}\.?$/;

    // Check for duplicate domains (case-insensitive)
    const domainCounts = new Map<string, number[]>();
    data.domains.forEach((domain, index) => {
      const normalized = domain.trim().toLowerCase();
      if (!domainCounts.has(normalized)) {
        domainCounts.set(normalized, []);
      }
      domainCounts.get(normalized)!.push(index);
    });

    // Add error for duplicate domains
    domainCounts.forEach((indices, normalizedDomain) => {
      if (indices.length > 1) {
        if (!errors.domains) errors.domains = [];
        const originalDomain = data.domains[indices[0]].trim();
        errors.domains.push(
          `Domain "${originalDomain}" is duplicated. Each domain must be unique.`,
        );
      }
    });

    // Validate each domain's format
    data.domains.forEach((domain, index) => {
      const trimmedDomain = domain.trim();
      if (trimmedDomain.length < 3) {
        if (!errors.domains) errors.domains = [];
        errors.domains.push(
          `Domain ${index + 1} must be at least 3 characters long`,
        );
      } else if (trimmedDomain.length > 253) {
        if (!errors.domains) errors.domains = [];
        errors.domains.push(
          `Domain ${index + 1} must be at most 253 characters long`,
        );
      } else if (!domainRegex.test(trimmedDomain)) {
        if (!errors.domains) errors.domains = [];
        errors.domains.push(`Domain ${index + 1} has invalid format`);
      }
    });

    // Additional validation for Local Team specific fields
    if (data.type === "LSP") {
      // Validate supports_payroll_plus is required for LSP
      if (data.supports_payroll_plus === null) {
        if (!errors.supports_payroll_plus) {
          errors.supports_payroll_plus = [];
        }
        errors.supports_payroll_plus.push(
          "Does this Local Team support Payroll +? is required",
        );
      }

      // Validate payslip_portal_link when supports_payroll_plus is true
      if (data.supports_payroll_plus === true) {
        const trimmedLink = data.payslip_portal_link?.trim() || "";
        if (!trimmedLink) {
          if (!errors.payslip_portal_link) {
            errors.payslip_portal_link = [];
          }
          errors.payslip_portal_link.push(
            "Payslip Portal Link is required when Payroll + is supported",
          );
        } else {
          // Use comprehensive URL validation
          const urlValidation = isValidHttpsUrl(trimmedLink);
          if (!urlValidation.valid) {
            if (!errors.payslip_portal_link) {
              errors.payslip_portal_link = [];
            }
            errors.payslip_portal_link.push(
              urlValidation.error ||
                "Payslip Portal Link must be a valid HTTPS URL",
            );
          }
        }
      }
    }
  } catch (error) {
    console.error("Validation error:", error);
  }

  return errors;
}

/**
 * Check if Step 1 is valid
 */
export function isStep1Valid(data: TenantInfoFormData): boolean {
  const errors = validateStep1(data);
  return Object.keys(errors).length === 0;
}
